import { supabase } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────
// ZIFEK CORE — Socle des entités centrales réutilisables
// Catégories (17) · Tags (18) · Documents (19) · Média (20) · Calendrier (21)
// Tout module (Commerce, POS, Services, Finance, HR…) consomme ces
// fonctions au lieu de recréer ses propres listes.
// On réutilise l'existant (categorie / categorieservices / scategorie,
// documents, images, booking_*) et on n'ajoute que ce qui manque.
// ─────────────────────────────────────────────────────────────

function slugify(value: string | null | undefined, fallback: string): string {
  const base = (value || '').trim().toLowerCase();
  const slug = base
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug || fallback;
}

// ═════════════════════════════════════════════════════════════
// 17. CATÉGORIES — système réutilisable (catégorie / sous-catégorie / hiérarchie)
// ═════════════════════════════════════════════════════════════

export type CategoryKind = 'product' | 'service';

export interface SocleCategory {
  id: number;
  nom: string;
  description: string | null;
  idcommerce: number;
  parent_id: number | null;
  sort_order: number | null;
  slug: string | null;
  status: string | null;
  translation: Record<string, string> | null;
  kind: CategoryKind;
}

export interface SocleSubCategory {
  id: number;
  nom: string;
  description: string | null;
  idcategorie: number;
  nomcategorie: string | null;
  idcommerce: number;
  image: string | null;
  slug: string | null;
  status: string | null;
  translation: Record<string, string> | null;
}

function mapCategory(row: Record<string, unknown>, kind: CategoryKind): SocleCategory {
  return {
    id: Number(row.id),
    nom: String(row.nom ?? ''),
    description: (row.description as string) ?? null,
    idcommerce: Number(row.idcommerce ?? 0),
    parent_id: row.parent_id != null ? Number(row.parent_id) : null,
    sort_order: row.sort_order != null ? Number(row.sort_order) : null,
    slug: (row.slug as string) ?? null,
    status: (row.status as string) ?? 'active',
    translation: (row.translation as Record<string, string>) ?? null,
    kind,
  };
}

export async function listSocleCategories(commerceId: number, kind: CategoryKind): Promise<SocleCategory[]> {
  const table = kind === 'product' ? 'categorie' : 'categorieservices';
  const { data, error } = await supabase
    .from(table)
    .select('*')
    .eq('idcommerce', commerceId)
    .order('sort_order', { ascending: true, nullsFirst: false })
    .order('nom', { ascending: true });
  if (error) throw error;
  return (data || []).map((r) => mapCategory(r as Record<string, unknown>, kind));
}

export async function listSocleSubCategories(commerceId: number): Promise<SocleSubCategory[]> {
  const { data, error } = await supabase
    .from('scategorie')
    .select('*')
    .eq('idcommerce', commerceId)
    .order('nom', { ascending: true });
  if (error) throw error;
  return (data || []).map((r) => {
    const row = r as Record<string, unknown>;
    return {
      id: Number(row.id),
      nom: String(row.nom ?? ''),
      description: (row.description as string) ?? null,
      idcategorie: Number(row.idcategorie ?? 0),
      nomcategorie: (row.nomcategorie as string) ?? null,
      idcommerce: Number(row.idcommerce ?? 0),
      image: (row.image as string) ?? null,
      slug: (row.slug as string) ?? null,
      status: (row.status as string) ?? 'active',
      translation: (row.translation as Record<string, string>) ?? null,
    };
  });
}

export interface CategoryInput {
  commerceId: number;
  kind: CategoryKind;
  nom: string;
  description?: string | null;
  parentId?: number | null;
  image?: string | null;
}

export async function createSocleCategory(input: CategoryInput): Promise<number | null> {
  const table = input.kind === 'product' ? 'categorie' : 'categorieservices';
  const payload: Record<string, unknown> = {
    nom: input.nom,
    description: input.description || '',
    idcommerce: input.commerceId,
    afficher: 1,
    type: input.kind,
    image_couverture: input.image || '',
    parent_id: input.parentId ?? null,
    sort_order: 0,
    slug: slugify(input.nom, `cat-${Date.now()}`),
    status: 'active',
    translation: {},
  };
  const { data, error } = await supabase.from(table).insert(payload).select('id').maybeSingle();
  if (error) throw error;
  return (data?.id as number) ?? null;
}

export async function updateSocleCategory(
  id: number,
  kind: CategoryKind,
  patch: { nom?: string; description?: string | null; parentId?: number | null; slug?: string; status?: string; translation?: Record<string, string> },
): Promise<void> {
  const table = kind === 'product' ? 'categorie' : 'categorieservices';
  const payload: Record<string, unknown> = {};
  if (patch.nom !== undefined) payload.nom = patch.nom;
  if (patch.description !== undefined) payload.description = patch.description || '';
  if (patch.parentId !== undefined) payload.parent_id = patch.parentId ?? null;
  if (patch.slug !== undefined) payload.slug = slugify(patch.slug, `cat-${id}`);
  if (patch.status !== undefined) payload.status = patch.status;
  if (patch.translation !== undefined) payload.translation = patch.translation;
  if (Object.keys(payload).length === 0) return;
  const { error } = await supabase.from(table).update(payload).eq('id', id);
  if (error) throw error;
}

export async function setSocleCategoryStatus(id: number, kind: CategoryKind, status: string): Promise<void> {
  const table = kind === 'product' ? 'categorie' : 'categorieservices';
  const { error } = await supabase.from(table).update({ status }).eq('id', id);
  if (error) throw error;
}

export async function createSocleSubCategory(input: {
  commerceId: number;
  parentId: number;
  parentName: string;
  nom: string;
  description?: string | null;
}): Promise<number | null> {
  const { data, error } = await supabase
    .from('scategorie')
    .insert({
      nom: input.nom,
      description: input.description || '',
      idcategorie: input.parentId,
      nomcategorie: input.parentName,
      idcommerce: input.commerceId,
      image: '',
      slug: slugify(input.nom, `sub-${Date.now()}`),
      status: 'active',
      translation: {},
    })
    .select('id')
    .maybeSingle();
  if (error) throw error;
  return (data?.id as number) ?? null;
}

export async function setSocleSubCategoryStatus(id: number, status: string): Promise<void> {
  const { error } = await supabase.from('scategorie').update({ status }).eq('id', id);
  if (error) throw error;
}

// ═════════════════════════════════════════════════════════════
// 18. TAGS — système réutilisable et filtrable
// ═════════════════════════════════════════════════════════════

export interface CoreTag {
  id: number;
  tenant_id: number | null;
  organization_id: number | null;
  name: string;
  slug: string | null;
  color: string | null;
  scope: string | null;
  status: string | null;
  sort_order: number | null;
  created_at: string | null;
}

export interface CoreTagAssignment {
  id: number;
  tag_id: number;
  entity_type: string;
  entity_id: string;
  organization_id: number | null;
}

/** Couleurs de tag adossées aux rôles StyleSystem. */
export const TAG_COLORS: { key: string; label: string; className: string }[] = [
  { key: 'primary', label: 'Primaire', className: 'bg-primary-100 text-primary-900 border-primary-200' },
  { key: 'accent', label: 'Accent', className: 'bg-accent-100 text-accent-900 border-accent-200' },
  { key: 'secondary', label: 'Secondaire', className: 'bg-secondary-100 text-secondary-900 border-secondary-200' },
  { key: 'foreground', label: 'Neutre', className: 'bg-foreground-100 text-foreground-700 border-foreground-200' },
];

export function tagColorClass(key: string | null | undefined): string {
  return (TAG_COLORS.find((c) => c.key === key) || TAG_COLORS[2]).className;
}

/** Tags courants proposés en un clic. */
export const SUGGESTED_TAGS = ['premium', 'populaire', 'nouveau', 'recommandé', 'famille', 'business', 'urgent'];

/** Portées : à quoi un tag peut s'appliquer. */
export const TAG_SCOPES: { key: string; label: string }[] = [
  { key: 'both', label: 'Produits & services' },
  { key: 'product', label: 'Produits' },
  { key: 'service', label: 'Services' },
  { key: 'customer', label: 'Clients' },
  { key: 'contact', label: 'Contacts' },
];

export async function listCoreTags(organizationId: number): Promise<CoreTag[]> {
  const { data, error } = await supabase
    .from('core_tags')
    .select('*')
    .eq('organization_id', organizationId)
    .order('sort_order', { ascending: true })
    .order('name', { ascending: true });
  if (error) throw error;
  return (data || []) as CoreTag[];
}

export async function createCoreTag(input: {
  tenantId: number | null;
  organizationId: number;
  name: string;
  color?: string;
  scope?: string;
}): Promise<number | null> {
  const { data, error } = await supabase
    .from('core_tags')
    .insert({
      tenant_id: input.tenantId,
      organization_id: input.organizationId,
      name: input.name,
      slug: slugify(input.name, `tag-${Date.now()}`),
      color: input.color || 'secondary',
      scope: input.scope || 'both',
      status: 'active',
      sort_order: 0,
    })
    .select('id')
    .maybeSingle();
  if (error) throw error;
  return (data?.id as number) ?? null;
}

export async function updateCoreTag(
  id: number,
  patch: { name?: string; color?: string; scope?: string; status?: string },
): Promise<void> {
  const payload: Record<string, unknown> = {};
  if (patch.name !== undefined) {
    payload.name = patch.name;
    payload.slug = slugify(patch.name, `tag-${id}`);
  }
  if (patch.color !== undefined) payload.color = patch.color;
  if (patch.scope !== undefined) payload.scope = patch.scope;
  if (patch.status !== undefined) payload.status = patch.status;
  if (Object.keys(payload).length === 0) return;
  const { error } = await supabase.from('core_tags').update(payload).eq('id', id);
  if (error) throw error;
}

export async function deleteCoreTag(id: number): Promise<void> {
  await supabase.from('core_tag_assignments').delete().eq('tag_id', id);
  const { error } = await supabase.from('core_tags').delete().eq('id', id);
  if (error) throw error;
}

/** Nombre d'utilisations (assignations) par tag. */
export async function fetchTagUsage(organizationId: number): Promise<Record<number, number>> {
  const map: Record<number, number> = {};
  const { data } = await supabase
    .from('core_tag_assignments')
    .select('tag_id')
    .eq('organization_id', organizationId);
  (data || []).forEach((r) => {
    const id = Number((r as { tag_id: number }).tag_id);
    map[id] = (map[id] || 0) + 1;
  });
  return map;
}

export async function listTagIdsForEntity(entityType: string, entityId: string | number): Promise<number[]> {
  const { data } = await supabase
    .from('core_tag_assignments')
    .select('tag_id')
    .eq('entity_type', entityType)
    .eq('entity_id', String(entityId));
  return (data || []).map((r) => Number((r as { tag_id: number }).tag_id));
}

export async function setEntityTags(
  organizationId: number,
  entityType: string,
  entityId: string | number,
  tagIds: number[],
): Promise<void> {
  await supabase
    .from('core_tag_assignments')
    .delete()
    .eq('entity_type', entityType)
    .eq('entity_id', String(entityId));
  if (tagIds.length === 0) return;
  await supabase.from('core_tag_assignments').insert(
    tagIds.map((tagId) => ({
      tag_id: tagId,
      entity_type: entityType,
      entity_id: String(entityId),
      organization_id: organizationId,
    })),
  );
}

// ═════════════════════════════════════════════════════════════
// 19. DOCUMENTS — couche documentaire centrale (métadonnées)
// Les fichiers eux-mêmes restent dans le stockage Zifek.
// ═════════════════════════════════════════════════════════════

export interface CoreDocument {
  id: number;
  user_id: string;
  name: string;
  doc_type: string;
  content: unknown;
  folder_id: number | null;
  file_id: string | null;
  owner: number | null;
  entity_type: string | null;
  entity_id: string | null;
  category: string | null;
  visibility: string | null;
  uploaded_by: number | null;
  created_at: string | null;
  updated_at: string | null;
}

/** Entités auxquelles un document peut être rattaché (spec 19). */
export const DOCUMENT_ENTITY_TYPES: { key: string; label: string; icon: string }[] = [
  { key: 'user', label: 'Utilisateur', icon: 'ri-user-3-line' },
  { key: 'organization', label: 'Entreprise', icon: 'ri-building-2-line' },
  { key: 'customer', label: 'Client', icon: 'ri-user-star-line' },
  { key: 'order', label: 'Commande', icon: 'ri-shopping-bag-3-line' },
  { key: 'invoice', label: 'Facture', icon: 'ri-file-list-3-line' },
  { key: 'project', label: 'Projet', icon: 'ri-folders-line' },
  { key: 'reservation', label: 'Réservation', icon: 'ri-calendar-check-line' },
  { key: 'employee', label: 'Employé', icon: 'ri-team-line' },
  { key: 'product', label: 'Produit', icon: 'ri-shopping-bag-line' },
  { key: 'service', label: 'Service', icon: 'ri-service-line' },
];

export const VISIBILITIES: { key: string; label: string; icon: string }[] = [
  { key: 'private', label: 'Privé', icon: 'ri-lock-line' },
  { key: 'organization', label: 'Entreprise', icon: 'ri-building-4-line' },
  { key: 'public', label: 'Public', icon: 'ri-global-line' },
];

export function visibilityMeta(key: string | null | undefined) {
  return VISIBILITIES.find((v) => v.key === key) || VISIBILITIES[0];
}

export function entityTypeMeta(key: string | null | undefined) {
  return DOCUMENT_ENTITY_TYPES.find((v) => v.key === key) || null;
}

export async function listCoreDocuments(ownerId: number): Promise<CoreDocument[]> {
  const { data, error } = await supabase
    .from('documents')
    .select('*')
    .eq('owner', ownerId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []) as unknown as CoreDocument[];
}

export async function createCoreDocument(input: {
  ownerId: number;
  ownerUserId: string;
  name: string;
  docType?: string;
  entityType?: string | null;
  entityId?: string | null;
  category?: string | null;
  visibility?: string;
  fileId?: string | null;
}): Promise<number | null> {
  const { data, error } = await supabase
    .from('documents')
    .insert({
      user_id: input.ownerUserId,
      name: input.name,
      doc_type: input.docType || 'file',
      content: null,
      owner: input.ownerId,
      uploaded_by: input.ownerId,
      entity_type: input.entityType || null,
      entity_id: input.entityId || null,
      category: input.category || null,
      visibility: input.visibility || 'private',
      file_id: input.fileId || null,
    })
    .select('id')
    .maybeSingle();
  if (error) throw error;
  return (data?.id as number) ?? null;
}

export async function updateCoreDocument(
  id: number,
  patch: {
    name?: string;
    entityType?: string | null;
    entityId?: string | null;
    category?: string | null;
    visibility?: string;
    fileId?: string | null;
  },
): Promise<void> {
  const payload: Record<string, unknown> = { updated_at: new Date().toISOString() };
  if (patch.name !== undefined) payload.name = patch.name;
  if (patch.entityType !== undefined) payload.entity_type = patch.entityType || null;
  if (patch.entityId !== undefined) payload.entity_id = patch.entityId || null;
  if (patch.category !== undefined) payload.category = patch.category || null;
  if (patch.visibility !== undefined) payload.visibility = patch.visibility;
  if (patch.fileId !== undefined) payload.file_id = patch.fileId || null;
  const { error } = await supabase.from('documents').update(payload).eq('id', id);
  if (error) throw error;
}

export async function deleteCoreDocument(id: number): Promise<void> {
  const { error } = await supabase.from('documents').delete().eq('id', id);
  if (error) throw error;
}

// ═════════════════════════════════════════════════════════════
// 20. MÉDIA — gestion centralisée (image / vidéo / audio / PDF / document)
// Réutilise la table `images` en l'enrichissant.
// ═════════════════════════════════════════════════════════════

export interface CoreMedia {
  id: number;
  title: string;
  description: string | null;
  filepath: string;
  uploaded_date: string | null;
  id_user: number;
  user_name: string | null;
  categorie: string | null;
  media_type: string | null;
  mime_type: string | null;
  size: number | null;
  storage_path: string | null;
  metadata: Record<string, unknown> | null;
  visibility: string | null;
  tenant_id: number | null;
  organization_id: number | null;
  owner: number | null;
}

export const MEDIA_TYPES: { key: string; label: string; icon: string }[] = [
  { key: 'image', label: 'Image', icon: 'ri-image-line' },
  { key: 'video', label: 'Vidéo', icon: 'ri-movie-line' },
  { key: 'audio', label: 'Audio', icon: 'ri-music-2-line' },
  { key: 'pdf', label: 'PDF', icon: 'ri-file-pdf-line' },
  { key: 'document', label: 'Document', icon: 'ri-file-text-line' },
];

export function mediaTypeMeta(key: string | null | undefined) {
  return MEDIA_TYPES.find((m) => m.key === key) || MEDIA_TYPES[0];
}

/** Devine le type de média depuis un nom de fichier / mime. */
export function guessMediaType(name: string | null | undefined, mime?: string | null): string {
  const ext = (name || '').split('.').pop()?.toLowerCase() || '';
  if (mime?.startsWith('video') || ['mp4', 'webm', 'mov', 'avi'].includes(ext)) return 'video';
  if (mime?.startsWith('audio') || ['mp3', 'wav', 'ogg', 'm4a'].includes(ext)) return 'audio';
  if (mime === 'application/pdf' || ext === 'pdf') return 'pdf';
  if (['png', 'jpg', 'jpeg', 'webp', 'gif', 'svg'].includes(ext) || mime?.startsWith('image')) return 'image';
  return 'document';
}

export async function listCoreMedia(ownerId: number): Promise<CoreMedia[]> {
  const { data, error } = await supabase
    .from('images')
    .select('*')
    .eq('owner', ownerId)
    .order('uploaded_date', { ascending: false });
  if (error) throw error;
  return (data || []) as unknown as CoreMedia[];
}

export async function createCoreMedia(input: {
  ownerId: number;
  ownerName: string;
  title: string;
  filepath: string;
  mediaType?: string;
  mimeType?: string | null;
  size?: number;
  storagePath?: string | null;
  visibility?: string;
  tenantId?: number | null;
  organizationId?: number | null;
  category?: string | null;
}): Promise<number | null> {
  const { data, error } = await supabase
    .from('images')
    .insert({
      title: input.title,
      description: '',
      filepath: input.filepath,
      uploaded_date: new Date().toISOString(),
      id_user: input.ownerId,
      user_name: input.ownerName,
      categorie: input.category || '',
      media_type: input.mediaType || guessMediaType(input.title, input.mimeType),
      mime_type: input.mimeType || null,
      size: input.size || 0,
      storage_path: input.storagePath || null,
      metadata: {},
      visibility: input.visibility || 'private',
      tenant_id: input.tenantId ?? null,
      organization_id: input.organizationId ?? null,
      owner: input.ownerId,
    })
    .select('id')
    .maybeSingle();
  if (error) throw error;
  return (data?.id as number) ?? null;
}

export async function updateCoreMedia(
  id: number,
  patch: { title?: string; mediaType?: string; visibility?: string; category?: string | null },
): Promise<void> {
  const payload: Record<string, unknown> = {};
  if (patch.title !== undefined) payload.title = patch.title;
  if (patch.mediaType !== undefined) payload.media_type = patch.mediaType;
  if (patch.visibility !== undefined) payload.visibility = patch.visibility;
  if (patch.category !== undefined) payload.categorie = patch.category || '';
  if (Object.keys(payload).length === 0) return;
  const { error } = await supabase.from('images').update(payload).eq('id', id);
  if (error) throw error;
}

export async function deleteCoreMedia(id: number): Promise<void> {
  const { error } = await supabase.from('images').delete().eq('id', id);
  if (error) throw error;
}

// ═════════════════════════════════════════════════════════════
// 21. CALENDRIER — base centrale (Calendar / Event / Availability / Time Slot)
// Réutilise booking_services (Calendar), booking_availabilities (Availability),
// booking_appointments (RDV) et ajoute core_calendar_events pour
// réunions / tâches / événements internes.
// ═════════════════════════════════════════════════════════════

export interface CoreCalendar {
  id: number;
  owner: number;
  service_name: string;
  duration: number | null;
  location: string | null;
  description: string | null;
  statut: number | null;
}

export interface CoreAvailability {
  id: number;
  service_id: number;
  day_of_week: string;
  start_time: string;
  end_time: string;
}

export interface CoreCalendarEvent {
  id: number;
  tenant_id: number | null;
  organization_id: number | null;
  calendar_id: number | null;
  title: string;
  description: string | null;
  event_type: string | null;
  start_at: string;
  end_at: string | null;
  all_day: boolean | null;
  location: string | null;
  employee_id: number | null;
  entity_type: string | null;
  entity_id: string | null;
  status: string | null;
  created_at: string | null;
}

export const CALENDAR_EVENT_TYPES: { key: string; label: string; icon: string; className: string }[] = [
  { key: 'appointment', label: 'Rendez-vous', icon: 'ri-calendar-check-line', className: 'bg-primary-100 text-primary-900' },
  { key: 'reservation', label: 'Réservation', icon: 'ri-hotel-line', className: 'bg-accent-100 text-accent-900' },
  { key: 'meeting', label: 'Réunion', icon: 'ri-group-line', className: 'bg-secondary-100 text-secondary-900' },
  { key: 'task', label: 'Tâche', icon: 'ri-checkbox-line', className: 'bg-background-100 text-foreground-700' },
  { key: 'event', label: 'Événement', icon: 'ri-calendar-event-line', className: 'bg-accent-100 text-accent-900' },
  { key: 'availability', label: 'Disponibilité', icon: 'ri-time-line', className: 'bg-secondary-100 text-secondary-900' },
];

export function calendarEventTypeMeta(key: string | null | undefined) {
  return CALENDAR_EVENT_TYPES.find((t) => t.key === key) || CALENDAR_EVENT_TYPES[4];
}

export const EVENT_STATUSES: { key: string; label: string }[] = [
  { key: 'planned', label: 'Planifié' },
  { key: 'confirmed', label: 'Confirmé' },
  { key: 'done', label: 'Terminé' },
  { key: 'cancelled', label: 'Annulé' },
];

export async function listCoreCalendars(commerceId: number): Promise<CoreCalendar[]> {
  const { data, error } = await supabase
    .from('booking_services')
    .select('id, owner, service_name, duration, location, description, statut')
    .eq('owner', commerceId)
    .order('id', { ascending: true });
  if (error) throw error;
  return (data || []) as CoreCalendar[];
}

export async function listCalendarAvailabilities(serviceId: number): Promise<CoreAvailability[]> {
  const { data, error } = await supabase
    .from('booking_availabilities')
    .select('*')
    .eq('service_id', serviceId);
  if (error) throw error;
  return (data || []) as CoreAvailability[];
}

export async function listCoreCalendarEvents(organizationId: number): Promise<CoreCalendarEvent[]> {
  const { data, error } = await supabase
    .from('core_calendar_events')
    .select('*')
    .eq('organization_id', organizationId)
    .order('start_at', { ascending: true });
  if (error) throw error;
  return (data || []) as CoreCalendarEvent[];
}

export interface CalendarEventInput {
  tenantId: number | null;
  organizationId: number;
  calendarId?: number | null;
  title: string;
  description?: string | null;
  eventType?: string;
  startAt: string;
  endAt?: string | null;
  allDay?: boolean;
  location?: string | null;
  employeeId?: number | null;
  entityType?: string | null;
  entityId?: string | null;
  status?: string;
}

export async function createCoreCalendarEvent(input: CalendarEventInput): Promise<number | null> {
  const { data, error } = await supabase
    .from('core_calendar_events')
    .insert({
      tenant_id: input.tenantId,
      organization_id: input.organizationId,
      calendar_id: input.calendarId ?? null,
      title: input.title,
      description: input.description || null,
      event_type: input.eventType || 'event',
      start_at: input.startAt,
      end_at: input.endAt || null,
      all_day: !!input.allDay,
      location: input.location || null,
      employee_id: input.employeeId ?? null,
      entity_type: input.entityType || null,
      entity_id: input.entityId || null,
      status: input.status || 'planned',
    })
    .select('id')
    .maybeSingle();
  if (error) throw error;
  return (data?.id as number) ?? null;
}

export async function updateCoreCalendarEvent(id: number, input: Partial<CalendarEventInput>): Promise<void> {
  const payload: Record<string, unknown> = {};
  if (input.title !== undefined) payload.title = input.title;
  if (input.description !== undefined) payload.description = input.description || null;
  if (input.eventType !== undefined) payload.event_type = input.eventType;
  if (input.startAt !== undefined) payload.start_at = input.startAt;
  if (input.endAt !== undefined) payload.end_at = input.endAt || null;
  if (input.allDay !== undefined) payload.all_day = !!input.allDay;
  if (input.location !== undefined) payload.location = input.location || null;
  if (input.employeeId !== undefined) payload.employee_id = input.employeeId ?? null;
  if (input.status !== undefined) payload.status = input.status;
  if (Object.keys(payload).length === 0) return;
  const { error } = await supabase.from('core_calendar_events').update(payload).eq('id', id);
  if (error) throw error;
}

export async function deleteCoreCalendarEvent(id: number): Promise<void> {
  const { error } = await supabase.from('core_calendar_events').delete().eq('id', id);
  if (error) throw error;
}